/**
 * The hidden debug panel: triple-tap the distance readout (`ProductOverview.md` §4.9).
 *
 * This is field-test equipment, not a developer nicety. Standing on a beach wondering why the
 * arrow is wrong, the only useful questions are: what does my GPS actually say, what does the
 * compass say, how old is their last fix, and is the socket even up.
 */

import { el } from '../lib/dom';

export interface DebugRow {
  label: string;
  value: string;
  /** Marks a row as the likely culprit. */
  warn?: boolean;
}

export interface DebugPanel {
  element: HTMLElement;
  update: (rows: DebugRow[]) => void;
  toggle: () => void;
  readonly visible: boolean;
  destroy: () => void;
}

export function createDebugPanel(): DebugPanel {
  const body = el('dl', { class: 'debug__rows' });
  let visible = false;

  const element = el(
    'aside',
    { class: 'debug', hidden: true, 'aria-label': 'Debug readout' },
    el(
      'header',
      { class: 'debug__head' },
      el('span', { class: 'debug__title', text: 'debug' }),
      el(
        'button',
        {
          class: 'debug__close',
          type: 'button',
          'aria-label': 'Close debug panel',
          onclick: () => panel.toggle(),
        },
        '✕',
      ),
    ),
    body,
  );

  const panel: DebugPanel = {
    element,
    get visible() {
      return visible;
    },
    update: (rows) => {
      if (!visible) return;
      body.replaceChildren(
        ...rows.flatMap((row) => [
          el('dt', { class: 'debug__label', text: row.label }),
          el('dd', {
            class: `debug__value mono${row.warn ? ' debug__value--warn' : ''}`,
            text: row.value,
          }),
        ]),
      );
    },
    toggle: () => {
      visible = !visible;
      element.toggleAttribute('hidden', !visible);
    },
    destroy: () => element.remove(),
  };

  return panel;
}

/**
 * Wire a triple-tap on an element to an action.
 *
 * Deliberately obscure — this must never fire by accident while someone is walking — but
 * reachable with cold hands, so the window is generous.
 */
export function onTripleTap(target: HTMLElement, action: () => void, windowMs = 700): () => void {
  let taps = 0;
  let timer: number | null = null;

  const handler = (): void => {
    taps++;
    if (timer !== null) window.clearTimeout(timer);

    if (taps >= 3) {
      taps = 0;
      action();
      return;
    }

    timer = window.setTimeout(() => {
      taps = 0;
      timer = null;
    }, windowMs);
  };

  target.addEventListener('click', handler);

  return () => {
    target.removeEventListener('click', handler);
    if (timer !== null) window.clearTimeout(timer);
  };
}
